import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  HealthAndSafety as InsuranceIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Upload as UploadIcon,
  Help as HelpIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DocumentIcon,
  LocalPolice as PoliceIcon,
  DirectionsCar as LicenseIcon,
  Warning as WarningIcon,
  Gavel as LegalIcon,
  AccountBalance as BankIcon,
  Verified as VerifiedIcon,
  CloudUpload as CloudUploadIcon,
  Map as MapIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Apartment as CompanyIcon,
  Assignment as PolicyIcon,
} from '@mui/icons-material';

/**
 * InsuranceVerification component for verifying insurance details for transporters and logistics companies
 * Provides a comprehensive verification process for various types of insurance required for logistics operations
 * @returns React component for insurance verification
 */
const InsuranceVerification: React.FC = () => {
  // State for tracking the current step in the verification process
  const [activeStep, setActiveStep] = useState(0);
  
  // State for loading status
  const [loading, setLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for document preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{name: string, file: File | null}>({name: '', file: null});
  
  // State for verification status dialog
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  
  // State for storing insurance verification data
  const [insuranceData, setInsuranceData] = useState({
    // Entity Information
    entityType: 'company', // 'company' or 'individual'
    entityName: '',
    entityId: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    
    // Vehicle Insurance
    hasVehicleInsurance: false,
    vehicleInsuranceProvider: '',
    vehicleInsurancePolicyNumber: '',
    vehicleInsuranceVerified: false,
    vehicleInsuranceExpiryDate: '',
    vehicleInsuranceCoverage: '',
    vehicleInsuranceAmount: '',
    vehicleInsuranceType: '',
    vehicleInsuranceDeductible: '',
    vehicleInsuranceVehicles: [] as string[],
    
    // Goods in Transit Insurance
    hasGoodsInTransitInsurance: false,
    goodsInTransitInsuranceProvider: '',
    goodsInTransitPolicyNumber: '',
    goodsInTransitInsuranceVerified: false,
    goodsInTransitExpiryDate: '',
    goodsInTransitCoverage: '',
    goodsInTransitAmount: '',
    goodsInTransitDeductible: '',
    goodsInTransitRestrictions: '',
    
    // Public Liability Insurance
    hasLiabilityInsurance: false,
    liabilityInsuranceProvider: '',
    liabilityInsurancePolicyNumber: '',
    liabilityInsuranceVerified: false,
    liabilityInsuranceExpiryDate: '',
    liabilityInsuranceCoverage: '',
    liabilityInsuranceAmount: '',
    liabilityInsuranceDeductible: '',
    
    // Workers' Compensation Insurance
    hasWorkersCompensation: false,
    workersCompensationProvider: '',
    workersCompensationPolicyNumber: '',
    workersCompensationVerified: false,
    workersCompensationExpiryDate: '',
    workersCompensationCoverage: '',
    workersCompensationAmount: '',
    
    // Professional Indemnity Insurance
    hasProfessionalIndemnity: false,
    professionalIndemnityProvider: '',
    professionalIndemnityPolicyNumber: '',
    professionalIndemnityVerified: false,
    professionalIndemnityExpiryDate: '',
    professionalIndemnityCoverage: '',
    professionalIndemnityAmount: '',
    
    // Verification Status
    overallInsuranceStatus: 'pending', // 'pending', 'verified', 'incomplete', 'expired'
    verificationNotes: '',
    verificationDate: '',
    verifiedBy: '',
    nextReviewDate: '',
  });
  
  // State for file uploads
  const [documents, setDocuments] = useState({
    vehicleInsuranceCertificate: null as File | null,
    goodsInTransitInsuranceCertificate: null as File | null,
    liabilityInsuranceCertificate: null as File | null,
    workersCompensationCertificate: null as File | null,
    professionalIndemnityCertificate: null as File | null,
  });
  
  // Steps in the verification process
  const steps = [
    'Entity Information',
    'Vehicle Insurance',
    'Goods in Transit Insurance',
    'Workers Compensation',
    'Professional Indemnity',
    'Verification Summary'
  ];
  
  // Insurance providers
  const insuranceProviders = [
    'Jubilee Insurance',
    'APA Insurance',
    'Britam',
    'CIC Insurance',
    'Heritage Insurance',
    'Madison Insurance',
    'UAP Insurance',
    'ICEA Lion',
    'GA Insurance',
    'Sanlam Insurance',
    'Other'
  ];
  
  // Vehicle insurance types
  const vehicleInsuranceTypes = [
    'Comprehensive',
    'Third Party',
    'Third Party Fire & Theft',
    'Commercial Vehicle',
    'Fleet Insurance'
  ];
  
  /**
   * Handles changes to form fields
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  const handleChange = (field: string, value: any) => {
    setInsuranceData({
      ...insuranceData,
      [field]: value
    });
  };
  
  /**
   * Handles changes to checkbox arrays (multiple selection)
   * @param field - The field name to update
   * @param value - The value to toggle in the array
   */
  const handleArrayChange = (field: string, value: string) => {
    const currentArray = insuranceData[field as keyof typeof insuranceData] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setInsuranceData({
      ...insuranceData,
      [field]: newArray
    });
  };
  
  /**
   * Handles file uploads for document verification
   * @param documentType - The type of document being uploaded
   * @param file - The file object from the input
   */
  const handleFileUpload = (documentType: string, file: File | null) => {
    setDocuments({
      ...documents,
      [documentType]: file
    });
  };
  
  /**
   * Opens the document preview dialog
   * @param documentName - The name of the document to preview
   * @param file - The file to preview
   */
  const handlePreviewDocument = (documentName: string, file: File | null) => {
    setPreviewDocument({ name: documentName, file });
    setPreviewOpen(true);
  };
  
  /**
   * Closes the document preview dialog
   */
  const handleClosePreview = () => {
    setPreviewOpen(false);
  };
  
  /**
   * Opens the verification status dialog
   */
  const handleOpenVerificationDialog = () => {
    setVerificationDialogOpen(true);
  };
  
  /**
   * Closes the verification status dialog
   */
  const handleCloseVerificationDialog = () => {
    setVerificationDialogOpen(false);
  };
  
  /**
   * Moves to the next step in the verification process
   */
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };
  
  /**
   * Moves to the previous step in the verification process
   */
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  /**
   * Submits the verification form
   */
  const handleSubmit = () => {
    setLoading(true);
    setError(null);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSuccess('Insurance verification completed successfully! The verification status has been updated.');
      
      // Calculate overall insurance status based on verification results
      const mandatoryInsurance = [
        insuranceData.vehicleInsuranceVerified,
        insuranceData.goodsInTransitInsuranceVerified,
        insuranceData.liabilityInsuranceVerified
      ];
      
      const additionalInsurance = [
        insuranceData.workersCompensationVerified,
        insuranceData.professionalIndemnityVerified
      ];
      
      const mandatoryVerified = mandatoryInsurance.every(item => item === true);
      const additionalVerifiedCount = additionalInsurance.filter(item => item === true).length;
      
      let overallStatus = 'pending';
      
      if (mandatoryVerified) {
        if (additionalVerifiedCount >= 1) {
          overallStatus = 'verified';
        } else {
          overallStatus = 'incomplete';
        }
      } else {
        overallStatus = 'incomplete';
      }
      
      // Check for expired policies
      const today = new Date();
      const expiryDates = [
        insuranceData.vehicleInsuranceExpiryDate,
        insuranceData.goodsInTransitExpiryDate,
        insuranceData.liabilityInsuranceExpiryDate,
        insuranceData.workersCompensationExpiryDate,
        insuranceData.professionalIndemnityExpiryDate
      ];
      
      const hasExpiredPolicy = expiryDates.some(date => {
        if (!date) return false;
        return new Date(date) < today;
      });
      
      if (hasExpiredPolicy) {
        overallStatus = 'expired';
      }
      
      // Update insurance data with verification results
      setInsuranceData({
        ...insuranceData,
        overallInsuranceStatus: overallStatus,
        verificationDate: new Date().toISOString().split('T')[0],
        verifiedBy: 'System Verification',
        nextReviewDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 months later
      });
      
      // In a real app, this would send the data to the server
      console.log('Insurance Data:', insuranceData);
      console.log('Documents:', documents);
    }, 2000);
  };
  
  /**
   * Renders the entity information step
   * @returns JSX for the entity information step
   */
  const renderEntityInformation = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Entity Information
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Provide information about the entity (company or individual) for which insurance verification is being performed.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="entity-type-label">Entity Type</InputLabel>
            <Select
              labelId="entity-type-label"
              value={insuranceData.entityType}
              label="Entity Type"
              onChange={(e) => handleChange('entityType', e.target.value)}
            >
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="individual">Individual (Transporter)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label={insuranceData.entityType === 'company' ? "Company Name" : "Transporter Name"}
            value={insuranceData.entityName}
            onChange={(e) => handleChange('entityName', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label={insuranceData.entityType === 'company' ? "Registration Number" : "ID/Passport Number"}
            value={insuranceData.entityId}
            onChange={(e) => handleChange('entityId', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Contact Information
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Contact Person Name"
            value={insuranceData.contactName}
            onChange={(e) => handleChange('contactName', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Contact Phone Number"
            value={insuranceData.contactPhone}
            onChange={(e) => handleChange('contactPhone', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Contact Email"
            type="email"
            value={insuranceData.contactEmail}
            onChange={(e) => handleChange('contactEmail', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
  
  /**
   * Renders the vehicle insurance step
   * @returns JSX for the vehicle insurance step
   */
  const renderVehicleInsurance = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Vehicle Insurance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify vehicle insurance details. All logistics companies and transporters must have valid vehicle insurance.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={insuranceData.hasVehicleInsurance}
                onChange={(e) => handleChange('hasVehicleInsurance', e.target.checked)}
              />
            }
            label="Has Vehicle Insurance"
          />
        </Grid>
        
        {insuranceData.hasVehicleInsurance && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="vehicle-insurance-provider-label">Insurance Provider</InputLabel>
                <Select
                  labelId="vehicle-insurance-provider-label"
                  value={insuranceData.vehicleInsuranceProvider}
                  label="Insurance Provider"
                  onChange={(e) => handleChange('vehicleInsuranceProvider', e.target.value)}
                >
                  {insuranceProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Policy Number"
                value={insuranceData.vehicleInsurancePolicyNumber}
                onChange={(e) => handleChange('vehicleInsurancePolicyNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="vehicle-insurance-type-label">Insurance Type</InputLabel>
                <Select
                  labelId="vehicle-insurance-type-label"
                  value={insuranceData.vehicleInsuranceType}
                  label="Insurance Type"
                  onChange={(e) => handleChange('vehicleInsuranceType', e.target.value)}
                >
                  {vehicleInsuranceTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={insuranceData.vehicleInsuranceExpiryDate}
                onChange={(e) => handleChange('vehicleInsuranceExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coverage Details"
                value={insuranceData.vehicleInsuranceCoverage}
                onChange={(e) => handleChange('vehicleInsuranceCoverage', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Amount"
                value={insuranceData.vehicleInsuranceAmount}
                onChange={(e) => handleChange('vehicleInsuranceAmount', e.target.value)}
                InputProps={{
                  startAdornment: <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deductible Amount"
                value={insuranceData.vehicleInsuranceDeductible}
                onChange={(e) => handleChange('vehicleInsuranceDeductible', e.target.value)}
                InputProps={{
                  startAdornment: <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={insuranceData.vehicleInsuranceVerified}
                    onChange={(e) => handleChange('vehicleInsuranceVerified', e.target.checked)}
                  />
                }
                label="Insurance Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Insurance Certificate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('vehicleInsuranceCertificate', file);
                    }}
                  />
                </Button>
                {documents.vehicleInsuranceCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.vehicleInsuranceCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => previewDocument.file ? window.open(URL.createObjectURL(previewDocument.file)) : undefined}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the goods in transit insurance step
   * @returns JSX for the goods in transit insurance step
   */
  const renderGoodsInTransitInsurance = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Goods in Transit Insurance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify goods in transit insurance details. This insurance covers goods during transportation against loss or damage.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={insuranceData.hasGoodsInTransitInsurance}
                onChange={(e) => handleChange('hasGoodsInTransitInsurance', e.target.checked)}
              />
            }
            label="Has Goods in Transit Insurance"
          />
        </Grid>
        
        {insuranceData.hasGoodsInTransitInsurance && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="goods-insurance-provider-label">Insurance Provider</InputLabel>
                <Select
                  labelId="goods-insurance-provider-label"
                  value={insuranceData.goodsInTransitInsuranceProvider}
                  label="Insurance Provider"
                  onChange={(e) => handleChange('goodsInTransitInsuranceProvider', e.target.value)}
                >
                  {insuranceProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Policy Number"
                value={insuranceData.goodsInTransitPolicyNumber}
                onChange={(e) => handleChange('goodsInTransitPolicyNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={insuranceData.goodsInTransitExpiryDate}
                onChange={(e) => handleChange('goodsInTransitExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coverage Details"
                value={insuranceData.goodsInTransitCoverage}
                onChange={(e) => handleChange('goodsInTransitCoverage', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Amount"
                value={insuranceData.goodsInTransitAmount}
                onChange={(e) => handleChange('goodsInTransitAmount', e.target.value)}
                InputProps={{
                  startAdornment: <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deductible Amount"
                value={insuranceData.goodsInTransitDeductible}
                onChange={(e) => handleChange('goodsInTransitDeductible', e.target.value)}
                InputProps={{
                  startAdornment: <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Restrictions or Exclusions"
                multiline
                rows={3}
                value={insuranceData.goodsInTransitRestrictions}
                onChange={(e) => handleChange('goodsInTransitRestrictions', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={insuranceData.goodsInTransitInsuranceVerified}
                    onChange={(e) => handleChange('goodsInTransitInsuranceVerified', e.target.checked)}
                  />
                }
                label="Insurance Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Insurance Certificate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('goodsInTransitInsuranceCertificate', file);
                    }}
                  />
                </Button>
                {documents.goodsInTransitInsuranceCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.goodsInTransitInsuranceCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => previewDocument.file ? window.open(URL.createObjectURL(previewDocument.file)) : undefined}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the workers' compensation insurance step
   * @returns JSX for the workers' compensation insurance step
   */
  const renderWorkersCompensation = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Workers' Compensation Insurance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify workers' compensation insurance details. This insurance covers employees in case of work-related injuries or illnesses.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={insuranceData.hasWorkersCompensation}
                onChange={(e) => handleChange('hasWorkersCompensation', e.target.checked)}
              />
            }
            label="Has Workers' Compensation Insurance"
          />
        </Grid>
        
        {insuranceData.hasWorkersCompensation && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="workers-comp-provider-label">Insurance Provider</InputLabel>
                <Select
                  labelId="workers-comp-provider-label"
                  value={insuranceData.workersCompensationProvider}
                  label="Insurance Provider"
                  onChange={(e) => handleChange('workersCompensationProvider', e.target.value)}
                >
                  {insuranceProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Policy Number"
                value={insuranceData.workersCompensationPolicyNumber}
                onChange={(e) => handleChange('workersCompensationPolicyNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={insuranceData.workersCompensationExpiryDate}
                onChange={(e) => handleChange('workersCompensationExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coverage Details"
                value={insuranceData.workersCompensationCoverage}
                onChange={(e) => handleChange('workersCompensationCoverage', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Amount"
                value={insuranceData.workersCompensationAmount}
                onChange={(e) => handleChange('workersCompensationAmount', e.target.value)}
                InputProps={{
                  startAdornment: <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={insuranceData.workersCompensationVerified}
                    onChange={(e) => handleChange('workersCompensationVerified', e.target.checked)}
                  />
                }
                label="Insurance Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Insurance Certificate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('workersCompensationCertificate', file);
                    }}
                  />
                </Button>
                {documents.workersCompensationCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.workersCompensationCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => previewDocument.file ? window.open(URL.createObjectURL(previewDocument.file)) : undefined}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the professional indemnity insurance step
   * @returns JSX for the professional indemnity insurance step
   */
  const renderProfessionalIndemnity = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Professional Indemnity Insurance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify professional indemnity insurance details. This insurance covers the company against claims of negligence or inadequate services.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={insuranceData.hasProfessionalIndemnity}
                onChange={(e) => handleChange('hasProfessionalIndemnity', e.target.checked)}
              />
            }
            label="Has Professional Indemnity Insurance"
          />
        </Grid>
        
        {insuranceData.hasProfessionalIndemnity && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="indemnity-provider-label">Insurance Provider</InputLabel>
                <Select
                  labelId="indemnity-provider-label"
                  value={insuranceData.professionalIndemnityProvider}
                  label="Insurance Provider"
                  onChange={(e) => handleChange('professionalIndemnityProvider', e.target.value)}
                >
                  {insuranceProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Policy Number"
                value={insuranceData.professionalIndemnityPolicyNumber}
                onChange={(e) => handleChange('professionalIndemnityPolicyNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={insuranceData.professionalIndemnityExpiryDate}
                onChange={(e) => handleChange('professionalIndemnityExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coverage Details"
                value={insuranceData.professionalIndemnityCoverage}
                onChange={(e) => handleChange('professionalIndemnityCoverage', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Amount"
                value={insuranceData.professionalIndemnityAmount}
                onChange={(e) => handleChange('professionalIndemnityAmount', e.target.value)}
                InputProps={{
                  startAdornment: <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={insuranceData.professionalIndemnityVerified}
                    onChange={(e) => handleChange('professionalIndemnityVerified', e.target.checked)}
                  />
                }
                label="Insurance Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Insurance Certificate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('professionalIndemnityCertificate', file);
                    }}
                  />
                </Button>
                {documents.professionalIndemnityCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.professionalIndemnityCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => previewDocument.file ? window.open(URL.createObjectURL(previewDocument.file)) : undefined}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the verification summary step
   * @returns JSX for the verification summary step
   */
  const renderVerificationSummary = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Insurance Verification Summary
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Review the insurance verification summary and submit the verification results.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Entity Information
              </Typography>
              <Typography variant="body2">
                <strong>Entity Type:</strong> {insuranceData.entityType === 'company' ? 'Company' : 'Individual (Transporter)'}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {insuranceData.entityName}
              </Typography>
              <Typography variant="body2">
                <strong>ID/Registration:</strong> {insuranceData.entityId}
              </Typography>
              <Typography variant="body2">
                <strong>Contact Person:</strong> {insuranceData.contactName}
              </Typography>
              <Typography variant="body2">
                <strong>Contact Phone:</strong> {insuranceData.contactPhone}
              </Typography>
              <Typography variant="body2">
                <strong>Contact Email:</strong> {insuranceData.contactEmail}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="div">
                <LicenseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Vehicle Insurance
                {insuranceData.vehicleInsuranceVerified ? (
                  <Chip size="small" label="Verified" color="success" sx={{ ml: 1 }} />
                ) : (
                  <Chip size="small" label="Not Verified" color="default" sx={{ ml: 1 }} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {insuranceData.hasVehicleInsurance ? (
                <>
                  <Typography variant="body2">
                    <strong>Provider:</strong> {insuranceData.vehicleInsuranceProvider}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Policy Number:</strong> {insuranceData.vehicleInsurancePolicyNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {insuranceData.vehicleInsuranceType}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiry Date:</strong> {insuranceData.vehicleInsuranceExpiryDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Coverage:</strong> {insuranceData.vehicleInsuranceCoverage}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {insuranceData.vehicleInsuranceAmount}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No vehicle insurance information provided.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="div">
                <TruckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Goods in Transit Insurance
                {insuranceData.goodsInTransitInsuranceVerified ? (
                  <Chip size="small" label="Verified" color="success" sx={{ ml: 1 }} />
                ) : (
                  <Chip size="small" label="Not Verified" color="default" sx={{ ml: 1 }} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {insuranceData.hasGoodsInTransitInsurance ? (
                <>
                  <Typography variant="body2">
                    <strong>Provider:</strong> {insuranceData.goodsInTransitInsuranceProvider}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Policy Number:</strong> {insuranceData.goodsInTransitPolicyNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiry Date:</strong> {insuranceData.goodsInTransitExpiryDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Coverage:</strong> {insuranceData.goodsInTransitCoverage}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {insuranceData.goodsInTransitAmount}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Restrictions:</strong> {insuranceData.goodsInTransitRestrictions}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No goods in transit insurance information provided.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="div">
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Public Liability Insurance
                {insuranceData.liabilityInsuranceVerified ? (
                  <Chip size="small" label="Verified" color="success" sx={{ ml: 1 }} />
                ) : (
                  <Chip size="small" label="Not Verified" color="default" sx={{ ml: 1 }} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {insuranceData.hasLiabilityInsurance ? (
                <>
                  <Typography variant="body2">
                    <strong>Provider:</strong> {insuranceData.liabilityInsuranceProvider}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Policy Number:</strong> {insuranceData.liabilityInsurancePolicyNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiry Date:</strong> {insuranceData.liabilityInsuranceExpiryDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Coverage:</strong> {insuranceData.liabilityInsuranceCoverage}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {insuranceData.liabilityInsuranceAmount}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No public liability insurance information provided.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="div">
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Workers' Compensation Insurance
                {insuranceData.workersCompensationVerified ? (
                  <Chip size="small" label="Verified" color="success" sx={{ ml: 1 }} />
                ) : (
                  <Chip size="small" label="Not Verified" color="default" sx={{ ml: 1 }} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {insuranceData.hasWorkersCompensation ? (
                <>
                  <Typography variant="body2">
                    <strong>Provider:</strong> {insuranceData.workersCompensationProvider}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Policy Number:</strong> {insuranceData.workersCompensationPolicyNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiry Date:</strong> {insuranceData.workersCompensationExpiryDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Coverage:</strong> {insuranceData.workersCompensationCoverage}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {insuranceData.workersCompensationAmount}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No workers' compensation insurance information provided.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="div">
                <PolicyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Professional Indemnity Insurance
                {insuranceData.professionalIndemnityVerified ? (
                  <Chip size="small" label="Verified" color="success" sx={{ ml: 1 }} />
                ) : (
                  <Chip size="small" label="Not Verified" color="default" sx={{ ml: 1 }} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {insuranceData.hasProfessionalIndemnity ? (
                <>
                  <Typography variant="body2">
                    <strong>Provider:</strong> {insuranceData.professionalIndemnityProvider}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Policy Number:</strong> {insuranceData.professionalIndemnityPolicyNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiry Date:</strong> {insuranceData.professionalIndemnityExpiryDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Coverage:</strong> {insuranceData.professionalIndemnityCoverage}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {insuranceData.professionalIndemnityAmount}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No professional indemnity insurance information provided.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Verification Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Verification Notes"
            value={insuranceData.verificationNotes}
            onChange={(e) => handleChange('verificationNotes', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
  
  /**
   * Renders the current step in the verification process
   * @returns JSX for the current step
   */
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderEntityInformation();
      case 1:
        return renderVehicleInsurance();
      case 2:
        return renderGoodsInTransitInsurance();
      case 3:
        return renderWorkersCompensation();
      case 4:
        return renderProfessionalIndemnity();
      case 5:
        return renderVerificationSummary();
      default:
        return 'Unknown step';
    }
  };
  
  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom align="center">
          <InsuranceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Insurance Verification System
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ my: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        <Stepper activeStep={activeStep} sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ mt: 4 }}>
          {getStepContent(activeStep)}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            disabled={loading}
          >
            {loading ? 'Processing...' : activeStep === steps.length - 1 ? 'Submit' : 'Next'}
          </Button>
        </Box>
      </Paper>
      
      {/* Document Preview Dialog */}
      <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>
          {previewDocument.name} Preview
        </DialogTitle>
        <DialogContent dividers>
          {previewDocument.file && (
            previewDocument.file.type.includes('image') ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img 
                  src={URL.createObjectURL(previewDocument.file)} 
                  alt={previewDocument.name} 
                  style={{ maxWidth: '100%', maxHeight: '70vh' }} 
                />
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body1">
                  Preview not available for this file type. Please download the file to view.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => previewDocument.file ? window.open(URL.createObjectURL(previewDocument.file)) : undefined}
                  sx={{ mt: 2 }}
                >
                  Download File
                </Button>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Verification Status Dialog */}
      <Dialog open={verificationDialogOpen} onClose={handleCloseVerificationDialog}>
        <DialogTitle>Verification Status</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerificationDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InsuranceVerification;